// MuseStrip — the wall's identity slot (plan 2026_09_18_armed_frame_budget.md § D6, board
// `OvidMuseFlow.dc.html`'s `.bar`/`.muses`/`.thumbs`). Same token set the pack pages' `--ov-*` custom
// properties already carry (`styles/index.scss`).

// The strip's own way of saying "this one" — a mixin, not a placeholder: `.thumbOn` below and the
// closed chip's hover/focus state (`.chip` below) both call this once rather than each carrying its
// own `outline: 1px solid var(--ov-ember)`. A mixin, not `@extend`, because the chip's own call sites
// sit inside `@media (hover: hover)` — Sass refuses to `@extend` a placeholder across a media-query
// boundary.
@mixin museOutline {
    outline: 1px solid var(--ov-ember);
}

// The bar's own identity chip (plan `2026_09_18_ovid_wall_polish.md` § P4, board
// `OvidArmed.dc.html`'s `.thumbs`/`.th`/`.mname`) — square thumb + her name, one button, no "As",
// no "Change" word (decision 3: a tap reopens the strip in place). Unchanged by § D6.
//
// Design ruling (operator-validated, 2026-09-18): Render stays the bar's only FILLED element — the
// chip gains signifiers, not weight. No fill, no border box, no pill on the chip itself; instead a
// chevron after her name (`.chipChevron`) and, on hover/focus, the strip's OWN ember outline on the
// thumb (`museOutline` above, shared with `.thumbOn` — the highlighted thumb inside the open strip
// carries the identical rule, so the chip reads as a preview of the exact affordance the strip uses).
//
// `min-height: 44px` gives the button a real tap target beyond the 40px thumb; `margin: -2px 0`
// cancels the 4px it would otherwise add to the chip's own flow height, the same padding/margin
// technique `.track` below uses to grow a box without moving anything around it — the verb button
// is the bar's tallest child at ~42px (bar itself carries 10px vertical padding), so without this
// the 44px chip would become the new tallest child and grow the bar by 2px.
.chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    min-width: 0;
    min-height: 44px;
    padding-right: 8px;
    margin: -2px 0;

    // The thumb's own outline (below) IS the focus indicator — no second ring drawn around the
    // whole button.
    &:focus-visible {
        outline: none;
    }

    @media (hover: hover) {
        &:hover .chipThumb {
            @include museOutline;
            outline-offset: 2px;
        }

        &:hover .chipChevron {
            color: var(--ov-ivory);
        }
    }

    &:focus-visible .chipThumb {
        @include museOutline;
        outline-offset: 2px;
    }

    &:focus-visible .chipChevron {
        color: var(--ov-ivory);
    }
}

.chipThumb {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    background: var(--ov-raise);
    object-fit: cover;
    display: block;
}

.chipName {
    font-family: var(--ov-display);
    font-style: italic;
    font-weight: 500;
    font-size: 15px;
    color: var(--ov-ivory);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 140px;
}

// The chosen name with one of hers being made beneath it: three short rows inside the chip's 40px.
.chipStack {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    max-width: 180px;
    text-align: left;

    .chipName {
        line-height: 1.1;
    }
}

.chipSub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9.5px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ov-dim);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

// The chevron says the chip can be pressed — a signifier, not a weight: no fill, no border, just a
// dim stroke that lifts to `--ov-ivory` alongside the thumb's own hover/focus outline (`.chip`
// above).
.chipChevron {
    flex: none;
    color: var(--ov-dim);
    margin-left: -2px;
    transition: color var(--ov-dur-1) var(--ov-ease);
}

// The open strip (§ D6, board `.muses`) — takes the bar's own `.idOpen` slot (`Column`/
// `Sheet.module.scss`), which is already `flex: 1`; this wrapper only lays out its own two children.
.strip {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    width: 100%;
}

// One horizontally-scrolling row, no wrap. Native overflow scroll — a focused thumb (Tab) scrolls
// itself into view with no extra JS (browser default for a focusable descendant of an
// `overflow: auto` box); `onScroll` (`MuseStrip.tsx`) is what decides when `.more` hides.
.track {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    overflow-x: auto;
    scrollbar-width: none;
    // A scroll box clips whatever is drawn outside its padding box, and `.thumbOn`'s outline stands
    // 3px outside the thumb (`outline-offset: 2px` + 1px) — cut top and bottom on dev, 2026-09-18.
    // The padding makes room for it; the equal negative margin keeps the bar's height and the first
    // thumb's position where they were.
    padding: 3px;
    margin: -3px;

    &::-webkit-scrollbar {
        display: none;
    }
}

// 44x58 (plan § D6) — a selector, not a portrait (principle 5): no name caption, only the
// highlighted/chosen outline says which one is which.
.thumb {
    flex: none;
    width: 44px;
    height: 58px;
    padding: 0;
    border: none;
    background: var(--ov-raise);
    cursor: pointer;
    outline-offset: 2px;


    &[aria-busy='true'] {
        opacity: 0.6;
    }
}

// Highlighted (hovered, keyboard-focused, else the chosen one) — the ONE outlined thumb at a time
// (plan § D6: "the chosen/highlighted one"), never a second colour for "chosen but not highlighted".
// Shares `museOutline` (top of file) with the closed chip's own hover/focus state.
.thumbOn {
    @include museOutline;
}

.thumbFrame {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    background: var(--ov-raise);
}

// `+N` (plan § D6) — mono, like the board's own `.more`; flex: none so it never scrolls with the
// track, `.strip`'s own flex row places it after.
.more {
    flex: none;
    padding: 0 4px;
    border: none;
    background: none;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ov-dim);
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        color: var(--ov-ash);
    }
}

.error {
    margin: 0;
    font-family: var(--ov-grotesque);
    font-size: 12px;
    color: var(--ov-error);
}

// ── a muse of her own in the strip (boards `OvidOwnMuseFlow.dc.html` steps 1, 3, 5) ──────────────────────────────

// One thumb and, on her own, the x that lets her go. The x shows on hover or focus where there is a pointer; a phone
// reaches the same question by a long press (`MuseStrip.tsx`).
.cell {
    position: relative;
    flex: none;
    display: flex;
}

.x {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 1px solid var(--ov-line-strong);
    border-radius: var(--ov-radius-pill);
    background: var(--ov-panel);
    color: var(--ov-ash);
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    opacity: 0;
    transition: opacity var(--ov-dur-1) var(--ov-ease);

    &:hover,
    &:focus-visible {
        color: var(--ov-ivory);
        opacity: 1;
    }

    @media (hover: hover) {
        .cell:hover &,
        .cell:focus-within & {
            opacity: 1;
        }
    }

    // No hover on a phone: the x stays out of the strip, the long press carries the question.
    @media (hover: none) {
        display: none;
    }
}

// The + frame: the strip's own thumb size, dashed, the accent's `+` (board `.strip figure.new`).
.plus {
    border: 1px dashed color-mix(in srgb, var(--ov-ember) 70%, transparent);
    background: none;
    color: var(--ov-ember);
    font-family: var(--ov-display);
    font-size: 26px;
    line-height: 1;
}

// The hairline between her own muses and the roster.
.rule {
    flex: none;
    width: 1px;
    height: 40px;
    background: var(--ov-line);
}

// A muse still coming to life, in the strip: dimmed, a hairline along the foot (board `figure.cook`).
.cook {
    position: relative;

    .thumbFrame {
        opacity: 0.45;
    }
}

.cookTrack {
    position: absolute;
    left: 4px;
    right: 4px;
    bottom: 4px;
    height: 2px;
    border-radius: var(--ov-radius-pill);
    background: color-mix(in srgb, var(--ov-ivory) 18%, transparent);
    overflow: hidden;

    i {
        display: block;
        height: 100%;
        background: var(--ov-ember);
    }
}

// The closed slot while she comes to life (board step 3): her photo, the line, the hairline. It takes the slot's
// width up to the verb, as Narcis's training track does.
.training {
    flex: 1;
    padding-right: 0;
}

.prog {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    flex: 1;
    text-align: left;
}

.progLine {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ov-dim);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.progTrack {
    display: block;
    height: 2px;
    background: var(--ov-raise);
    border-radius: var(--ov-radius-pill);
    overflow: hidden;

    i {
        display: block;
        height: 100%;
        background: var(--ov-ember);
        border-radius: var(--ov-radius-pill);
    }
}

// The bar's one question (board step 5): the verb has left, the two answers stand in its place.
.ask {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}

.askText {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    color: var(--ov-ivory);

    span {
        color: var(--ov-ash);
    }
}

.keep,
.letGo {
    flex: none;
    font-family: var(--ov-font);
    font-size: 13.5px;
    padding: 10px 18px;
    border-radius: var(--ov-radius);
    white-space: nowrap;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
}

.keep {
    background: none;
    border: 1px solid var(--ov-line-strong);
    color: var(--ov-ivory);
}

.letGo {
    border: none;
    font-weight: 500;
    background: var(--ov-ember);
    color: var(--ov-on-ember);
}
